import { useState, type JSX } from 'react';

import PaymentElementsForm from '@/components/purchase/PaymentElementsForm';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { OnGenericPaymentSubmit } from '@/components/purchase/PurchaseForm/types';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import { SubscribePurchaseError, subscribePurchaseErrors } from '@/components/purchase/utils/Purchase';
import FreeTrialHeading from '@/components/subscribe/headings/FreeTrialHeading';
import LifetimeHeading from '@/components/subscribe/headings/LifetimeHeading';
import MonthlyHeading from '@/components/subscribe/headings/MonthlyHeading';
import YearlyHeading from '@/components/subscribe/headings/YearlyHeading';
import { useAnalytics } from '@/hooks/analytics';
import { useSubPurchase } from '@/hooks/purchase/useSubPurchase';
import { usePurchaseParamsState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { SubscriptionType } from '@/store/purchaseParams/types';

import { Wrapper } from './styles';

interface PaymentFormScreenProps {
	email?: string;
	onSuccess?: () => void;
	hidePriceAfterDisclaimer?: boolean;
}

const PaymentFormScreen = ({
	email,
	onSuccess: _onSuccess,
	hidePriceAfterDisclaimer = true,
}: PaymentFormScreenProps) => {
	const { isStripeNextEnabled } = useStripeElements();
	const purchaseParams = usePurchaseParamsState();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();

	const { plan } = purchaseParams;
	const [noSubscribeError] = useState(
		subscribePurchaseErrors.includes(purchaseParams?.purchaseType?.type as SubscribePurchaseError),
	);

	const submitSubPurchase = useSubPurchase();

	const onSuccess = async () => {
		if (_onSuccess) {
			_onSuccess();
		} else {
			await routerPush('/premium-confirmation');
		}
	};

	const onPaymentSubmit: OnGenericPaymentSubmit = async args => {
		return submitSubPurchase({
			...args,
			endpoint: email ? 'subscription/email' : 'subscription',
		});
	};

	// TODO: See if I need to add in the already subscribed headers here
	const headingComponents: Record<SubscriptionType, JSX.Element | null> = {
		freetrial: <FreeTrialHeading />,
		lifetime: <LifetimeHeading />,
		subscribe: <YearlyHeading />,
		monthly: <MonthlyHeading />,
		quarterly: null,
		family: null,
		'one-time-purchase': null,
		'already-subscribed': null,
		'already-trialing': null, // To Be Tested
		'previously-trialed': null, // To Be Tested
	};

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Landed',
			eventProps: {
				is_stripe_next: isStripeNextEnabled,
			},
		});

		if (purchaseParams?.purchaseType?.type === 'freetrial') {
			logEvent({
				eventName: 'Free Trial : Landed',
			});
		} else {
			logEvent({
				eventName: 'Subscribe : Landed',
				eventProps: {
					plan: purchaseParams?.plan,
				},
			});
		}
	});

	return (
		<Wrapper>
			{!noSubscribeError && headingComponents[purchaseParams?.purchaseType?.type || 'subscribe']}
			{isStripeNextEnabled ? (
				<PaymentElementsForm
					endpoint={email ? 'subscription/email' : 'subscription'}
					afterPurchaseSuccess={onSuccess}
					purchaseArgs={email ? { email } : {}}
					hidePriceAfterDisclaimer={hidePriceAfterDisclaimer}
				/>
			) : (
				<PurchaseForm
					alignment="center"
					centerFormElements
					buttonBgColor="gradientRainbow6"
					onSuccess={onSuccess}
					onPurchase={onPaymentSubmit}
					email={email}
					hidePriceAfterDisclaimer={hidePriceAfterDisclaimer}
					hideApplePay={plan === 'lifetime'}
				/>
			)}
		</Wrapper>
	);
};

export default PaymentFormScreen;
